import Node from '../node.js';
import { PARAM_TYPE } from '../connector.js';
import GlobalDefs from '../../../globalDefs.js';
import AssetCacher, { ASSET_TYPE } from '../assetCacher.js';
import Utils3D from '../../utils/utils3D.js';

export default class NodeMaterial extends Node
{
	constructor(pParent)
	{
		super(pParent);
		this.addInput(PARAM_TYPE.GEOMETRY, 'geo', null);
		
		this.addInput(PARAM_TYPE.FLOAT, 'opacity', 1.0, { minValue: 0, maxValue: 1});
		this.addInput(PARAM_TYPE.COLOR, 'diffuse', {r: 0.5, g: 0.5, b: 0.5});
		this.addInput(PARAM_TYPE.COLOR, 'emissive', {r: 0.0, g: 0.0, b: 0.0});
		this.addInput(PARAM_TYPE.COLOR, 'specular', {r: 0.0, g: 0.0, b: 0.0});
		this.addInput(PARAM_TYPE.FLOAT, 'specularPower', 128.0, { minValue: 0, maxValue: 256});
		this.addInputsToGroup('Colors', ['opacity', 'diffuse', 'emissive', 'specular', 'specularPower']);

		const pAssetProvider = this.getGraph().getAssetProvider();
		const pTextureChoices = pAssetProvider.getTextureList();

		this.addInput(PARAM_TYPE.DROPDOWN_TEXTURE, 'diffuse_texture', GlobalDefs.g_strNoneKey, { dropdownOptions: pTextureChoices });
		this.addInput(PARAM_TYPE.DROPDOWN_TEXTURE, 'bump_texture', GlobalDefs.g_strNoneKey, { dropdownOptions: pTextureChoices });
		this.addInput(PARAM_TYPE.FLOAT, 'bump_level', 1.0, { minValue: 0.01, maxValue: 2});
		this.addInput(PARAM_TYPE.FLOAT, 'uv_scale', 1.0, { minValue: 0.01, maxValue: 3});
		this.addInput(PARAM_TYPE.FLOAT, 'u_scale', 1.0, { minValue: 0.01, maxValue: 3});
		this.addInput(PARAM_TYPE.FLOAT, 'v_scale', 1.0, { minValue: 0.01, maxValue: 3});
		this.addInput(PARAM_TYPE.FLOAT, 'u_offset', 0.0, { minValue: 0, maxValue: 1});
		this.addInput(PARAM_TYPE.FLOAT, 'v_offset', 0.0, { minValue: 0, maxValue: 1});

		this.addInputsToGroup('Textures', ['diffuse_texture', 'bump_texture', 'bump_level', 'uv_scale', 'u_scale', 'v_scale', 'u_offset', 'v_offset']);

		this.addOutput(PARAM_TYPE.GEOMETRY, 'geo');
	}
	
	getType()
	{
		return 'material';
	}
	
	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{	
		let pGeoList = inputValues['geo'];
		if(!pGeoList || pGeoList.length === 0)
			return null;

		// Create the material
		this.createAndAssignMaterial(pGeoList[0], scene);

		let pMat = pGeoList[0].material;
		pMat.alpha = inputValues['opacity'];
		pMat.backFaceCulling = true;
		pMat.transparencyMode = BABYLON.Material.MATERIAL_ALPHABLEND;

		let SUPPORTED_TEXTURE_TYPES;
		if(pMat.diffuseColor)
		{
			SUPPORTED_TEXTURE_TYPES = {
				'diffuse_texture': 'diffuseTexture', 
				'bump_texture': 'bumpTexture'
			};

			if(inputValues['diffuse'])
				pMat.diffuseColor = new BABYLON.Color4(inputValues['diffuse'].r, inputValues['diffuse'].g, inputValues['diffuse'].b, 1.0);

			if(inputValues['emissive'])
				pMat.emissiveColor = new BABYLON.Color4(inputValues['emissive'].r, inputValues['emissive'].g, inputValues['emissive'].b, 1.0);

			if(inputValues['specular'])
				pMat.specularColor = new BABYLON.Color4(inputValues['specular'].r, inputValues['specular'].g, inputValues['specular'].b, 1.0);

			pMat.specularPower = inputValues['specularPower'];
		}
		else
		{
			SUPPORTED_TEXTURE_TYPES = {
				'diffuse_texture': 'albedoTexture', 
				'bump_texture': 'bumpTexture'
			};

			if(inputValues['diffuse'])
				pMat.albedoColor = new BABYLON.Color4(inputValues['diffuse'].r, inputValues['diffuse'].g, inputValues['diffuse'].b, 1.0);

			if(inputValues['emissive'])
				pMat.emissiveColor = new BABYLON.Color4(inputValues['emissive'].r, inputValues['emissive'].g, inputValues['emissive'].b, 1.0);
			
			pMat.specularIntensity = inputValues['specularPower'];
		}

		const pAssetProvider = this.getGraph().getAssetProvider();
		for(let textureType of Object.keys(SUPPORTED_TEXTURE_TYPES))
		{
			const strTextureValue = inputValues[textureType];
			if(strTextureValue && strTextureValue !== GlobalDefs.g_strNoneKey)
			{
				let pCachedTexture = AssetCacher.getInstance().getCachedAsset(ASSET_TYPE.TEXTURE, strTextureValue, scene, undefined, this);
				if(!pCachedTexture)
				{
					let pInfo = pAssetProvider.getTexture(strTextureValue);
					pCachedTexture = new BABYLON.Texture(pInfo.file, scene);

					AssetCacher.getInstance().storeAsset(ASSET_TYPE.TEXTURE, strTextureValue, pCachedTexture);
				}
				pMat[SUPPORTED_TEXTURE_TYPES[textureType]] = pCachedTexture;
				pMat[SUPPORTED_TEXTURE_TYPES[textureType]].uScale = inputValues['uv_scale']*inputValues['u_scale'];
				pMat[SUPPORTED_TEXTURE_TYPES[textureType]].vScale = inputValues['uv_scale']*inputValues['v_scale'];

				pMat[SUPPORTED_TEXTURE_TYPES[textureType]].uOffset = inputValues['u_offset'];
				pMat[SUPPORTED_TEXTURE_TYPES[textureType]].vOffset = inputValues['v_offset'];
			}
		}

		if(pMat.bumpTexture)
			pMat.bumpTexture.level = inputValues['bump_level'];			

		for(let pSrcMesh of pGeoList)
			pSrcMesh.material = pMat;

		Utils3D.freezeMaterials(pMat);

		return pGeoList;
	}
}
